import { PropertySheet, PropertySheetCategory, TypedCardElementPeer } from "adaptivecards-designer";
import * as Adaptive from "adaptivecards";
import React from "react";
import Select, { StylesConfig } from 'react-select';
import { createRoot } from 'react-dom/client';
import { Input } from "adaptivecards";
import { GetExpandedInputChoiceSet, handleValidationElement, initCustomRenderedElement, removeOtherPropertyConfigurations, SetTeamsExperiencePropertyConfigurations } from "../Utils/CustomElementUtils";
import { getPropertyEditorInstance, PresentationKeys, PropertyKeys } from "../Pickers/PickerBase";
import { AdaptiveCardTexts } from "../../../shared/utils/constants";

const displayLabelProperty = new Adaptive.StringProperty(Adaptive.Versions.v1_0, "displayLabel", true);
const displayChoicesProperty = new Adaptive.EnumProperty(Adaptive.Versions.v1_0, "displayChoices", []);
const requireFieldProperty = new Adaptive.BoolProperty(Adaptive.Versions.v1_0, "requireField", false);
const presentationProperty = new Adaptive.StringProperty(Adaptive.Versions.v1_0, "presentation", true);

export const colourStyles: StylesConfig<unknown, true> = {
    control: (styles) => ({
        ...styles,
        border: 'unset !important',
        boxShadow: 'unset !important',
        borderRadius: 'unset !important',
        background: 'var(--selected-container-background-color) !important',
        '& input': {
            color: 'var(--mgt-theme-foreground) !important',
        }
    }),
    singleValue: (styles, { data }) => {
        return {
        ...styles,
        fontSize: '0.8rem',
        color: 'var(--mgt-theme-foreground)'
        };
    },
    multiValue: (styles, { data }) => {
        return {
        ...styles,
        borderRadius: "15px",
        backgroundColor: "var(--selected-item-background-color)",
        paddingLeft: "3px"
        };
    },
    multiValueLabel: (styles, { data }) => ({
        ...styles,
        fontSize: '14px',
        fontWeight: 600,
        color: 'var(--mgt-theme-foreground)',
    }),
    multiValueRemove: (styles, { data, children }) => ({
        ...styles,
        color: 'var(--mgt-theme-foreground)',
        ':hover': {
            backgroundColor: 'transparent',
        },
        '& svg': {
            display: 'none !important'
        },
        backgroundImage: 'var(--selected-item-background-image)',
        backgroundSize: 'var(--selected-item-background-imageSize)',
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        paddingLeft: '8px',
        paddingRight: '22px',
    }),
    dropdownIndicator: (styles) => ({
        ...styles,
        padding: 'unset !important',
        color: 'initial !important',
        '& svg': {
            transform: 'scale(0.8) !important',
            display: 'none !important'
        },
    }),
    indicatorSeparator: (styles) => ({
        display: 'none'
    }),
    option: (styles, {isFocused}) => ({
        ...styles,
        backgroundColor: isFocused ? "var(--selected-item-hover-color)" : 'transparent',
        color: 'var(--mgt-theme-foreground)'
    }),
    menuPortal: (base) => ({ ...base, zIndex: 9999999 }),
    menuList: (styles) => ({
        ...styles,
        background: 'var(--selected-container-background-color)'
    })
};

export class CollaboratorsTypeahead extends Adaptive.TextInput {
    static readonly displayLabelProperty = displayLabelProperty;
    static readonly displayChoicesProperty = displayChoicesProperty;
    static readonly requireFieldProperty = requireFieldProperty;
    static readonly presentationProperty = presentationProperty;
    static readonly JsonTypeName = "Collaborators";
    static readonly ElementId = "Ticket.Collaborators";
    static readonly defaultLabel = "Collaborators";
    static pickerChoices: any[];
    static onCustomValueChange: (input: Input) => void;
    static disableField: boolean;
    static presentInTeams: boolean;

    //#region Schema

    @Adaptive.property(displayLabelProperty)
    get displayLabel(): string | undefined {
        return this.getValue(CollaboratorsTypeahead.displayLabelProperty);
    }

    set displayLabel(value: string | undefined) {
        this.setValue(CollaboratorsTypeahead.displayLabelProperty, value);
        this.label = value;
        this.updateLayout();
    }

    @Adaptive.property(displayChoicesProperty)
    get displayChoices(): any[] | undefined {
        return this.getValue(CollaboratorsTypeahead.displayChoicesProperty);
    }

    set displayChoices(value: any[] | undefined, ) {
        this.setValue(CollaboratorsTypeahead.displayChoicesProperty, value);
        this.updateLayout();
    }

    @Adaptive.property(requireFieldProperty)
    get requireField(): boolean | undefined {
        return this.getValue(CollaboratorsTypeahead.requireFieldProperty);
    }

    set requireField(value: boolean | undefined) {
        this.setValue(CollaboratorsTypeahead.requireFieldProperty, value);
        this.setValue(CollaboratorsTypeahead.isRequiredProperty, value);
        if(value)
            this.setValue(CollaboratorsTypeahead.errorMessageProperty, AdaptiveCardTexts.REQUIRE_FIELD);
        this.updateLayout();
    }

    @Adaptive.property(presentationProperty)
    get presentation(): string | undefined {
        return this.getValue(CollaboratorsTypeahead.presentationProperty);
    }

    set presentation(value: string | undefined) {
        this.setValue(CollaboratorsTypeahead.presentationProperty, value);
        this.updateLayout();
    }

    //#endregion

    protected internalRender(): HTMLElement | undefined {
        this.id = CollaboratorsTypeahead.ElementId;
        if((this?.displayLabel ?? "").length == 0)
            this.displayLabel = CollaboratorsTypeahead.defaultLabel;

        if(CollaboratorsTypeahead.pickerChoices){
            this.displayChoices = CollaboratorsTypeahead.pickerChoices;
        }

        if((this?.presentation ?? "").length == 0)
            this.presentation = PresentationKeys.lookup;

        if(this.presentation == PresentationKeys.lookup || !CollaboratorsTypeahead.presentInTeams){

            let renderedElement = initCustomRenderedElement("tikit-input tikit-typeahead");

            const options = this.displayChoices ?? [];

            let selectedValues = [];
            if(this.defaultValue){
                const valuesArr = this.defaultValue.split(",");
                selectedValues = options.filter(o => valuesArr.includes(o.value));
            }


            const domNode = document.createElement('div');
            const root = createRoot(domNode); 
            root.render(<Select placeholder={AdaptiveCardTexts.SEARCH_NAME} options={options} isClearable={false} menuPortalTarget={document.body} menuPosition="fixed" isMulti styles={colourStyles} defaultValue={selectedValues} {...CollaboratorsTypeahead.disableField && {isDisabled: true}}
                onChange={(newValue) => {
                    const values = newValue.map(x => x["value"]);
                    this.setValue(CollaboratorsTypeahead.valueProperty, values.join());
                    handleValidationElement(renderedElement);
                    CollaboratorsTypeahead.onCustomValueChange && CollaboratorsTypeahead.onCustomValueChange(this);
                }}
            />);

            renderedElement.append(domNode);

            return renderedElement;

        }else
            return GetExpandedInputChoiceSet(this);
    }

    get value(): string {
        return this.getValue(CollaboratorsTypeahead.valueProperty);
    }

    getJsonTypeName(): string {
        return CollaboratorsTypeahead.JsonTypeName;
    }

    updateLayout(processChildren: boolean = true) {
        super.updateLayout(processChildren);
    }
}

export class CollaboratorsTypeaheadPeer extends TypedCardElementPeer<CollaboratorsTypeahead> {
    static displayLabelProperty = getPropertyEditorInstance(CollaboratorsTypeahead.ElementId, PropertyKeys.displayLabel);
    static requireFieldProperty = getPropertyEditorInstance(CollaboratorsTypeahead.ElementId, PropertyKeys.requireField);
    static presentationProperty = getPropertyEditorInstance(CollaboratorsTypeahead.ElementId, PropertyKeys.presentation);
        
    populatePropertySheet(propertySheet: PropertySheet, defaultCategory: string = PropertySheetCategory.DefaultCategory) {
        
        super.populatePropertySheet(propertySheet, defaultCategory);
        removeOtherPropertyConfigurations(propertySheet);
        
        propertySheet.add(
            defaultCategory,
            CollaboratorsTypeaheadPeer.idProperty,
            CollaboratorsTypeaheadPeer.displayLabelProperty,
            CollaboratorsTypeaheadPeer.requireFieldProperty
        );
    
        SetTeamsExperiencePropertyConfigurations(propertySheet, [CollaboratorsTypeaheadPeer.presentationProperty]);
    }
}
